import {
  isCurrentAccountScopedStorageKey,
  listAccountScopedStorage,
  removeAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  readIsolatedRows,
  removeIsolatedRow,
  writeIsolatedRow,
  type IsolatedRowIo,
} from "@/shared/storage/isolated-row-state.mjs";
import {
  cancelTodoDeletion,
  queueTodoDeletion,
  type SyncTodoDeletion,
} from "@/shared/sync/deletion-outbox";
import { journalCurrentContentPushIntentSync } from "@/shared/sync/content-push-intent";
import {
  authoritativeRowIdsToPrune,
  syncRowsStructurallyEqual,
} from "@/shared/sync/authoritative-row-prune.mjs";
import { remoteTombstoneDeletesLocal } from "@/shared/sync/remote-tombstone-policy.mjs";
import {
  nextAccountRowMutationIso,
  ROW_MUTATION_DOMAINS,
} from "@/shared/sync/account-row-mutation-clock";
import {
  clearDeletedGoogleTaskCanonicalClientId,
  fenceDeletedGoogleTaskCanonicalClientId,
} from "@/shared/tasks/google-task-canonical-deletion-fence";

const TASK_ROW_PREFIX = "tb_public_web_task_v2:";
const TASKS_EVENT = "tb-public-web-tasks-changed";

export type TaskPriority = 0 | 1 | 2 | 3;

export type BriefTask = {
  id: string;
  text: string;
  notes?: string;
  priority: TaskPriority;
  dueDate?: string | null;
  completedAtEpochMs?: number | null;
  tag?: string;
  sortOrder: number;
  updatedAt: string;
};

export type SyncTodo = {
  clientId?: string;
  id?: string;
  text?: string;
  notes?: string;
  priority?: number;
  dueAtEpochMs?: number | null;
  completedAtEpochMs?: number | null;
  tag?: string;
  sortOrder?: number;
  updatedAt?: string;
};

export type ApplySyncTasksOptions = {
  merge?: boolean;
  authoritativeFullPull?: boolean;
  authoritativeBaseline?: BriefTask[];
  preferIncomingWhenLocalUnchanged?: boolean;
  preserveExistingLocalRows?: boolean;
};

function storage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function taskRowSlot(id: string): string {
  return encodeURIComponent(id);
}

const taskRowIo: IsolatedRowIo = {
  list() {
    return listAccountScopedStorage(TASK_ROW_PREFIX).map(({ baseKey, value }) => ({
      slot: baseKey.slice(TASK_ROW_PREFIX.length),
      value,
    }));
  },
  write(slot, value) {
    writeAccountScopedStorage(`${TASK_ROW_PREFIX}${slot}`, value);
  },
  remove(slot) {
    removeAccountScopedStorage(`${TASK_ROW_PREFIX}${slot}`);
  },
};

function newTaskId(): string {
  try {
    return `task-${crypto.randomUUID()}`;
  } catch {
    return `task-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function normalizePriority(value: unknown): TaskPriority {
  const n = Math.max(0, Math.min(3, Number(value) || 0));
  return n as TaskPriority;
}

function normalizeTask(task: Partial<BriefTask>, index: number): BriefTask | null {
  // Match the server/Android wire ceilings so pulling an Android task and later
  // editing a different task cannot silently shorten the original row.
  const text = String(task.text || "")
    .trim()
    .slice(0, 4_000);
  if (!text) return null;
  return {
    id: String(task.id || newTaskId()),
    text,
    notes: String(task.notes || "").slice(0, 8_000),
    priority: normalizePriority(task.priority),
    dueDate: task.dueDate || null,
    completedAtEpochMs: task.completedAtEpochMs || null,
    tag: String(task.tag || "").slice(0, 120),
    sortOrder: Number(task.sortOrder) || index,
    updatedAt: typeof task.updatedAt === "string" ? task.updatedAt : "",
  };
}

export function loadTasks(): BriefTask[] {
  const store = storage();
  if (!store) return [];
  try {
    return readIsolatedRows(taskRowIo)
      .map(({ slot, value }, index) => {
        const task = normalizeTask(value as Partial<BriefTask>, index);
        return task && taskRowSlot(task.id) === slot ? task : null;
      })
      .filter((task): task is BriefTask => Boolean(task))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.text.localeCompare(b.text));
  } catch {
    return [];
  }
}

function emitTasksChanged(): BriefTask[] {
  const tasks = loadTasks();
  tasks.forEach((task) => clearDeletedGoogleTaskCanonicalClientId(task.id));
  window.dispatchEvent(new CustomEvent(TASKS_EVENT, { detail: { tasks } }));
  return tasks;
}

function persistTaskRow(task: BriefTask, journalSyncIntent = true): void {
  if (journalSyncIntent) {
    journalCurrentContentPushIntentSync({ includeTasks: true });
  }
  writeIsolatedRow(taskRowIo, taskRowSlot(task.id), task);
  // A successfully persisted same-ID row is explicit undo/recreate authority,
  // whether it originated locally or from an authoritative account pull.
  clearDeletedGoogleTaskCanonicalClientId(task.id);
}

export function saveTasks(
  tasks: BriefTask[],
  options: { journalSyncIntent?: boolean } = {},
): BriefTask[] {
  const next = tasks.map((task, index) => ({
    ...task,
    sortOrder: index,
    updatedAt: task.updatedAt,
  }));
  const store = storage();
  if (store) {
    try {
      if (options.journalSyncIntent !== false) {
        journalCurrentContentPushIntentSync({ includeTasks: true });
      }
      next.forEach((task) => {
        writeIsolatedRow(taskRowIo, taskRowSlot(task.id), task);
        clearDeletedGoogleTaskCanonicalClientId(task.id);
      });
    } catch {
      /* local task writes are best effort */
    }
  }
  return emitTasksChanged();
}

export function addTask(input: {
  text: string;
  notes?: string;
  priority?: number;
  dueDate?: string | null;
  tag?: string;
}): BriefTask[] {
  const current = loadTasks();
  const task = normalizeTask(
    {
      id: newTaskId(),
      text: input.text,
      notes: input.notes,
      priority: normalizePriority(input.priority),
      dueDate: input.dueDate || null,
      tag: input.tag,
      completedAtEpochMs: null,
      sortOrder: current.length,
      updatedAt: new Date().toISOString(),
    },
    current.length,
  );
  if (!task) return current;
  persistTaskRow(task);
  return emitTasksChanged();
}

export function updateTask(id: string, patch: Partial<BriefTask>): BriefTask[] {
  const current = loadTasks();
  const existing = current.find((task) => task.id === id);
  if (!existing) return current;
  const now = nextAccountRowMutationIso(
    ROW_MUTATION_DOMAINS.todos,
    existing.id,
    existing.updatedAt,
  );
  const updated =
    normalizeTask({ ...existing, ...patch, updatedAt: now }, existing.sortOrder) ||
    existing;
  persistTaskRow(updated);
  return emitTasksChanged();
}

export function deleteTask(id: string): { next: BriefTask[]; deleted: BriefTask | null } {
  const current = loadTasks();
  const deleted = current.find((task) => task.id === id) || null;
  if (!deleted) return { next: current, deleted };
  queueTodoDeletion(deleted.id, deleted.updatedAt);
  fenceDeletedGoogleTaskCanonicalClientId(deleted.id);
  journalCurrentContentPushIntentSync({ includeTasks: true });
  removeIsolatedRow(taskRowIo, taskRowSlot(deleted.id));
  // Retry persistence after freeing the larger task row in case the first
  // dedicated fence write hit localStorage quota. The outbox remains the
  // read-time fallback until this deletion is acknowledged.
  fenceDeletedGoogleTaskCanonicalClientId(deleted.id);
  return { next: emitTasksChanged(), deleted };
}

export function restoreTask(task: BriefTask): BriefTask[] {
  const current = loadTasks();
  cancelTodoDeletion(task.id);
  if (current.some((item) => item.id === task.id)) {
    clearDeletedGoogleTaskCanonicalClientId(task.id);
    return current;
  }
  persistTaskRow({
    ...task,
    updatedAt: nextAccountRowMutationIso(
      ROW_MUTATION_DOMAINS.todos,
      task.id,
      task.updatedAt,
    ),
  });
  return emitTasksChanged();
}

export function completeTask(id: string, done: boolean): BriefTask[] {
  return updateTask(id, { completedAtEpochMs: done ? Date.now() : null });
}

export function dueDateToEpochMs(dueDate: string | null | undefined): number | null {
  if (!dueDate) return null;
  const date = new Date(`${dueDate}T12:00:00`);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

export function epochMsToDueDate(epochMs: number | null | undefined): string | null {
  if (!epochMs) return null;
  const date = new Date(epochMs);
  if (!Number.isFinite(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function tasksToSyncPayload(tasks = loadTasks()): SyncTodo[] {
  return tasks.map((task, index) => ({
    clientId: task.id,
    text: task.text,
    notes: task.notes || "",
    priority: task.priority,
    dueAtEpochMs: dueDateToEpochMs(task.dueDate),
    completedAtEpochMs: task.completedAtEpochMs || null,
    tag: task.tag || "",
    sortOrder: index,
    updatedAt: task.updatedAt || undefined,
  }));
}

function remoteTaskIsNewer(remote: BriefTask, current: BriefTask | undefined): boolean {
  if (!current) return true;
  const remoteMs = Date.parse(remote.updatedAt || "");
  const currentMs = Date.parse(current.updatedAt || "");
  if (!Number.isFinite(remoteMs)) return true;
  if (!Number.isFinite(currentMs)) return true;
  return remoteMs >= currentMs;
}

function mergeTasks(
  current: BriefTask[],
  incoming: BriefTask[],
  options: Pick<
    ApplySyncTasksOptions,
    | "authoritativeBaseline"
    | "preferIncomingWhenLocalUnchanged"
    | "preserveExistingLocalRows"
  > = {},
): BriefTask[] {
  const byId = new Map(current.map((task) => [task.id, task]));
  const hasAuthoritativeBaseline = Array.isArray(options.authoritativeBaseline);
  const baselineById = new Map(
    (options.authoritativeBaseline || []).map((task) => [task.id, task]),
  );
  incoming.forEach((task) => {
    const existing = byId.get(task.id);
    if (existing && options.preserveExistingLocalRows === true) return;
    if (options.preferIncomingWhenLocalUnchanged === true && hasAuthoritativeBaseline) {
      const baseline = baselineById.get(task.id);
      if (baseline && existing && syncRowsStructurallyEqual(baseline, existing)) {
        byId.set(task.id, task);
      } else if (!baseline && !existing) {
        byId.set(task.id, task);
      }
      // Any other baseline/current shape is an in-flight local insert, update,
      // or delete. Keep that exact local decision regardless of remote clocks;
      // its durable push intent and a later pull own convergence.
      return;
    }
    if (remoteTaskIsNewer(task, existing)) {
      byId.set(task.id, task);
    }
  });
  return [...byId.values()].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.text.localeCompare(b.text),
  );
}

export function applySyncTasks(
  todos: SyncTodo[] | null | undefined,
  options: ApplySyncTasksOptions = {},
): BriefTask[] {
  if (!Array.isArray(todos)) return loadTasks();
  const next = todos
    .slice()
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
    .map((todo, index) =>
      normalizeTask(
        {
          id: todo.clientId || todo.id,
          text: todo.text,
          notes: todo.notes,
          priority: normalizePriority(todo.priority),
          dueDate: epochMsToDueDate(todo.dueAtEpochMs),
          completedAtEpochMs: todo.completedAtEpochMs || null,
          tag: todo.tag,
          sortOrder: Number(todo.sortOrder) || index,
          updatedAt: todo.updatedAt || new Date().toISOString(),
        },
        index,
      ),
    )
    .filter((task): task is BriefTask => Boolean(task));
  const current = loadTasks();
  const resolved = options.merge ? mergeTasks(current, next, options) : next;
  const currentById = new Map(current.map((task) => [task.id, task]));
  let changed = false;
  for (const task of resolved) {
    clearDeletedGoogleTaskCanonicalClientId(task.id);
    if (syncRowsStructurallyEqual(currentById.get(task.id), task)) continue;
    persistTaskRow(task, false);
    changed = true;
  }
  if (options.authoritativeFullPull) {
    for (const id of authoritativeRowIdsToPrune(
      options.authoritativeBaseline || [],
      current,
      next,
      "id",
    )) {
      fenceDeletedGoogleTaskCanonicalClientId(id);
      removeIsolatedRow(taskRowIo, taskRowSlot(id));
      changed = true;
    }
  }
  return changed ? emitTasksChanged() : current;
}

export function applySyncTaskDeletions(
  deletions: Array<Partial<SyncTodoDeletion>> | null | undefined,
  options: {
    respectLocalUpdatedAt?: boolean;
    authoritativeBaseline?: BriefTask[];
    preferDeletionWhenLocalUnchanged?: boolean;
    preserveExistingLocalRows?: boolean;
  } = {},
): BriefTask[] {
  if (!Array.isArray(deletions) || deletions.length === 0) return loadTasks();
  const byId = new Map(
    deletions
      .map((item) => [String(item.clientId || "").trim(), item] as const)
      .filter(([id]) => Boolean(id)),
  );
  const current = loadTasks();
  const hasAuthoritativeBaseline = Array.isArray(options.authoritativeBaseline);
  const baselineById = new Map(
    (options.authoritativeBaseline || []).map((task) => [task.id, task]),
  );
  let changed = false;
  for (const task of current) {
    const deletion = byId.get(task.id);
    if (!deletion) continue;
    if (options.preserveExistingLocalRows === true) continue;
    if (options.preferDeletionWhenLocalUnchanged === true && hasAuthoritativeBaseline) {
      const baseline = baselineById.get(task.id);
      if (!baseline || !syncRowsStructurallyEqual(baseline, task)) continue;
      fenceDeletedGoogleTaskCanonicalClientId(task.id);
      removeIsolatedRow(taskRowIo, taskRowSlot(task.id));
      changed = true;
      continue;
    }
    const keep =
      options.respectLocalUpdatedAt === true &&
      !remoteTombstoneDeletesLocal(deletion.deletedAt, task.updatedAt);
    if (keep) continue;
    fenceDeletedGoogleTaskCanonicalClientId(task.id);
    removeIsolatedRow(taskRowIo, taskRowSlot(task.id));
    changed = true;
  }
  return changed ? emitTasksChanged() : current;
}

export function onTasksChanged(listener: () => void): () => void {
  window.addEventListener(TASKS_EVENT, listener);
  const onStorage = (event: StorageEvent) => {
    if (!isCurrentAccountScopedStorageKey(event.key, TASK_ROW_PREFIX)) return;
    loadTasks().forEach((task) => clearDeletedGoogleTaskCanonicalClientId(task.id));
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(TASKS_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}
